import clsx from "clsx";
import styles from "./StatusDot.module.css";

/**
 * Available colors for the StatusDot component.
 */
export type StatusDotColor = "green" | "emerald" | "amber" | "yellow" | "yellowMuted" | "red" | "blue" | "indigo" | "gray";

/**
 * Available sizes for the StatusDot component.
 */
export type StatusDotSize = "sm" | "md" | "lg";

/**
 * Props for the StatusDot component.
 */
export interface StatusDotProps {
  /** The color of the status dot. Defaults to "gray". */
  color?: StatusDotColor;
  /** The size of the status dot. Defaults to "md". */
  size?: StatusDotSize;
  /** Whether to show a pulsing animation. Defaults to false. Ignored when `ping` is true. */
  pulse?: boolean;
  /** Expanding ring ping (stronger than opacity `pulse`). Defaults to false. */
  ping?: boolean;
  /** Additional CSS classes to apply. */
  className?: string;
  /** Test ID for testing purposes. */
  "data-testid"?: string;
}

/**
 * Color class mappings for each status color variant.
 */
export const colorClasses: Record<StatusDotColor, string> = {
  green: "bg-green-600 text-green-600 dark:bg-green-500 dark:text-green-500",
  emerald: "bg-emerald-600 text-emerald-600 dark:bg-emerald-500 dark:text-emerald-500",
  amber: "bg-amber-500 text-amber-500",
  yellow: "bg-yellow-400 text-yellow-400 dark:bg-yellow-300 dark:text-yellow-300",
  yellowMuted: "bg-yellow-300/70 text-yellow-300/70 dark:bg-yellow-500/35 dark:text-yellow-500/35",
  red: "bg-red-500 text-red-500",
  blue: "bg-blue-500 text-blue-500",
  indigo: "bg-indigo-500 text-indigo-500 dark:bg-indigo-400 dark:text-indigo-400",
  gray: "bg-slate-400 text-slate-400",
};

/**
 * Size class mappings for each size variant.
 */
export const sizeClasses: Record<StatusDotSize, string> = {
  sm: "w-1.5 h-1.5",
  md: "w-2 h-2",
  lg: "w-2.5 h-2.5",
};

const pingRingClasses: Record<StatusDotColor, string> = {
  green: "bg-green-600/60 dark:bg-green-500/60",
  emerald: "bg-emerald-600/60 dark:bg-emerald-500/60",
  amber: "bg-amber-500/60",
  yellow: "bg-yellow-400/60 dark:bg-yellow-300/60",
  yellowMuted: "bg-yellow-300/50 dark:bg-yellow-500/40",
  red: "bg-red-500/60",
  blue: "bg-blue-500/60",
  indigo: "bg-indigo-500/60 dark:bg-indigo-400/60",
  gray: "bg-slate-400/60",
};

/**
 * A simple status indicator dot component.
 *
 * Displays a colored circular dot to indicate status. Commonly used alongside
 * icons, labels, or in lists to show the state of an item.
 *
 * @param color - The color variant of the dot (green, amber, red, indigo, blue, gray)
 * @param size - The size of the dot (sm, md, lg)
 * @param pulse - Whether to apply a pulsing animation
 * @param className - Additional CSS classes
 *
 * @example
 * // Basic usage
 * <StatusDot color="green" />
 *
 * @example
 * // With expanding ring ping for active/live state
 * <StatusDot color="amber" ping />
 *
 * @example
 * // Positioned absolutely next to an icon
 * <div className="relative">
 *   <FontAwesomeIcon icon={faDownload} />
 *   <StatusDot color="green" className="absolute -top-1 -right-1" />
 * </div>
 */
export function StatusDot({
  color = "gray",
  size = "md",
  pulse = false,
  ping = false,
  className,
  "data-testid": testId,
}: StatusDotProps) {
  if (ping) {
    return (
      <span
        className={clsx("relative flex flex-shrink-0", sizeClasses[size], className)}
        data-testid={testId}
        aria-hidden="true"
      >
        <span
          className={clsx(
            "absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 motion-reduce:animate-none",
            pingRingClasses[color],
          )}
        />
        <span
          className={clsx(
            "relative inline-flex rounded-full",
            sizeClasses[size],
            colorClasses[color],
          )}
        />
      </span>
    );
  }

  return (
    <span
      className={clsx(
        "inline-block rounded-full flex-shrink-0",
        colorClasses[color],
        sizeClasses[size],
        pulse && styles.pulse,
        className
      )}
      data-testid={testId}
      aria-hidden="true"
    />
  );
}
